import { gql } from "@apollo/client";

import { useToast } from "@src/lib/hooks/useToast";
import {
  ChatType,
  UserPushNotificationChatSetting,
  UserPushNotificationSettingType,
} from "@src/types.generated";

import {
  ChatNotificationOverrideDocument,
  ChatNotificationOverrideQuery,
  ChatNotificationOverrideQueryVariables,
  useChatNotificationOverrideQuery,
  useChatsPushNotificationSettingQuery,
  useDeleteChatNotificationOverrideMutation,
  useSetChatNotificationOverrideMutation,
} from "./useChatNotificationOverride.generated";

export const DEFAULT_CHATS_STATE = UserPushNotificationChatSetting.NewMessages;

export function useMutedChatIds(): Set<string> {
  const { data } = useChatNotificationOverrideQuery();
  return new Set(
    (data?.UserPushNotificationSettingChatOverrides ?? [])
      .filter((o) => o.chatSetting === UserPushNotificationChatSetting.Muted)
      .map((o) => o.chat.id),
  );
}

function useUserChatsDefault(
  chatType: ChatType,
): UserPushNotificationChatSetting {
  const { data } = useChatsPushNotificationSettingQuery();
  const settingType =
    chatType === ChatType.OneOnOne
      ? UserPushNotificationSettingType.OneOnOneChats
      : UserPushNotificationSettingType.Chats;
  const chatsSetting = data?.UserPushNotificationSettings.find(
    (s) => s.type === settingType,
  );
  return chatsSetting?.chatSetting ?? DEFAULT_CHATS_STATE;
}

export function useChatNotificationOverride(
  chatId: string,
  chatType: ChatType,
): {
  overrideChatSetting: UserPushNotificationChatSetting | null;
  userChatsDefault: UserPushNotificationChatSetting;
  effectiveChatSetting: UserPushNotificationChatSetting;
  muted: boolean;
  setChatSetting: (next: UserPushNotificationChatSetting) => void;
  resetChatSetting: () => void;
} {
  const { addErrorToast } = useToast();
  const userChatsDefault = useUserChatsDefault(chatType);
  const { data } = useChatNotificationOverrideQuery();
  const override = data?.UserPushNotificationSettingChatOverrides.find(
    (o) => o.chat.id === chatId,
  );
  const overrideChatSetting = override?.chatSetting ?? null;
  const effectiveChatSetting = overrideChatSetting ?? userChatsDefault;
  const muted = effectiveChatSetting === UserPushNotificationChatSetting.Muted;

  const [setOverrideMutation] = useSetChatNotificationOverrideMutation({
    onError: (error) => {
      addErrorToast({ ...error, callsite: "chat_notification_override" });
    },
  });
  const [deleteOverrideMutation] = useDeleteChatNotificationOverrideMutation({
    onError: (error) => {
      addErrorToast({ ...error, callsite: "chat_notification_override" });
    },
  });

  const setChatSetting = (next: UserPushNotificationChatSetting) => {
    if (next === effectiveChatSetting) return;
    if (override && next === userChatsDefault) {
      const overrideId = override.id;
      deleteOverrideMutation({
        variables: { id: overrideId },
        update: (cache, { data: mutationData }) => {
          if (
            !mutationData?.deleteUserPushNotificationSettingChatOverride.success
          ) {
            return;
          }
          cache.updateQuery<
            ChatNotificationOverrideQuery,
            ChatNotificationOverrideQueryVariables
          >({ query: ChatNotificationOverrideDocument }, (existing) => {
            const overrides =
              existing?.UserPushNotificationSettingChatOverrides ?? [];
            return {
              UserPushNotificationSettingChatOverrides: overrides.filter(
                (o) => o.id !== overrideId,
              ),
            };
          });
        },
      });
      return;
    }
    setOverrideMutation({
      variables: { input: { chatId, chatSetting: next } },
      update: (cache, { data: mutationData }) => {
        const updated =
          mutationData?.updateOrCreateUserPushNotificationSettingChatOverride
            .userPushNotificationSettingChatOverride;
        if (!updated) return;
        cache.updateQuery<
          ChatNotificationOverrideQuery,
          ChatNotificationOverrideQueryVariables
        >({ query: ChatNotificationOverrideDocument }, (existing) => {
          const overrides =
            existing?.UserPushNotificationSettingChatOverrides ?? [];
          const hasIt = overrides.some((o) => o.id === updated.id);
          return {
            UserPushNotificationSettingChatOverrides: hasIt
              ? overrides.map((o) => (o.id === updated.id ? updated : o))
              : [...overrides, updated],
          };
        });
      },
    });
  };

  const resetChatSetting = () => {
    if (!override) return;
    const overrideId = override.id;
    deleteOverrideMutation({
      variables: { id: overrideId },
      update: (cache, { data: mutationData }) => {
        if (
          !mutationData?.deleteUserPushNotificationSettingChatOverride.success
        ) {
          return;
        }
        cache.updateQuery<
          ChatNotificationOverrideQuery,
          ChatNotificationOverrideQueryVariables
        >({ query: ChatNotificationOverrideDocument }, (existing) => {
          const overrides =
            existing?.UserPushNotificationSettingChatOverrides ?? [];
          return {
            UserPushNotificationSettingChatOverrides: overrides.filter(
              (o) => o.id !== overrideId,
            ),
          };
        });
      },
    });
  };

  return {
    overrideChatSetting,
    userChatsDefault,
    effectiveChatSetting,
    muted,
    setChatSetting,
    resetChatSetting,
  };
}

gql`
  query ChatNotificationOverride {
    UserPushNotificationSettingChatOverrides {
      id
      chatSetting
      chat {
        id
      }
    }
  }

  query ChatsPushNotificationSetting {
    UserPushNotificationSettings {
      id
      type
      chatSetting
    }
  }

  mutation SetChatNotificationOverride(
    $input: UpdateOrCreateUserPushNotificationSettingChatOverrideInput!
  ) {
    updateOrCreateUserPushNotificationSettingChatOverride(input: $input) {
      success
      error {
        message
      }
      userPushNotificationSettingChatOverride {
        id
        chatSetting
        chat {
          id
        }
      }
    }
  }

  mutation DeleteChatNotificationOverride($id: UUID!) {
    deleteUserPushNotificationSettingChatOverride(id: $id) {
      success
      error {
        message
      }
    }
  }
`;
